'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  createContext,
  type ComponentProps,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { clsxm, type ClassValue } from '@vs-shop/utils';

import { Button } from '../../control/button';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
  disableKeyboardNavigation?: boolean;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  // `undefined` until embla has measured (SSR / first paint); then the real
  // scrollability. Nav buttons default to disabled while undefined so SSR and
  // the first client render agree (no hydration mismatch).
  canScrollPrev: boolean | undefined;
  canScrollNext: boolean | undefined;
} & CarouselProps;

const CarouselContext = createContext<CarouselContextProps | null>(null);

export function useCarousel() {
  const context = useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

function Carousel({
  orientation = 'horizontal',
  opts,
  setApi,
  plugins,
  disableKeyboardNavigation = false,
  className,
  children,
  ...props
}: ComponentProps<'div'> & CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      slidesToScroll: opts?.slidesToScroll ?? 'auto',
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    plugins
  );
  const [canScrollPrev, setCanScrollPrev] = useState<boolean | undefined>(undefined);
  const [canScrollNext, setCanScrollNext] = useState<boolean | undefined>(undefined);

  const onSelect = useCallback((emblaApi: CarouselApi) => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, []);

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (disableKeyboardNavigation) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [disableKeyboardNavigation, scrollPrev, scrollNext]
  );

  useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api.off('select', onSelect);
    };
  }, [api, onSelect]);

  const contextValue = useMemo(
    () => ({
      carouselRef,
      api,
      opts,
      orientation,
      scrollPrev,
      scrollNext,
      canScrollPrev,
      canScrollNext,
    }),
    [carouselRef, api, opts, orientation, scrollPrev, scrollNext, canScrollPrev, canScrollNext]
  );

  return (
    <CarouselContext.Provider value={contextValue}>
      <div
        onKeyDownCapture={handleKeyDown}
        className={clsxm('relative', className)}
        role="region"
        aria-roledescription="carousel"
        data-slot="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

function CarouselContent({ className, ...props }: ComponentProps<'div'>) {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div ref={carouselRef} className="overflow-hidden" data-slot="carousel-content">
      <div
        className={clsxm(
          'flex',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
          className
        )}
        {...props}
      />
    </div>
  );
}

function CarouselItem({ className, ...props }: ComponentProps<'div'>) {
  const { orientation } = useCarousel();

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={clsxm(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className
      )}
      {...props}
    />
  );
}

// Position is the only conditional dimension: `mid` absolutely centers the
// button against the carousel edge, `free` leaves it in normal flow for a
// caller-positioned toolbar. The left/right edge keys off `direction`.
// `navSize` is the control's own scale and treatment, distinct from the `size`
// this component forwards to `Button`: how big the target is, and — for
// `overlay` — that it hangs off the frame's edge over the artwork, in a
// translucent ground and the inverted ink, which is a treatment rather than a
// measurement. The chevron follows it, because a control and its mark scale
// together or the mark rattles inside the target.
const carouselNavVariants = cva('rounded-full', {
  variants: {
    direction: { prev: '', next: '' },
    position: { free: '', mid: 'absolute top-1/2 -translate-y-1/2' },
    navSize: {
      /** The smallest target: a rail whose controls sit beside its content. */
      base: 'h-8 w-8',
      /** A phone-sized target in a caller-positioned toolbar. */
      md: 'size-10',
      /** The desktop target. */
      lg: 'size-12',
      /** One control that grows with the viewport rather than being composed twice. */
      responsive: 'lap:size-12 size-10',
      /** Hung over the artwork: large, translucent, and outside the frame's edge. */
      overlay: 'bg-background/25 size-16 border-transparent',
    },
  },
  compoundVariants: [
    { direction: 'prev', position: 'mid', className: 'left-0' },
    { direction: 'next', position: 'mid', className: 'right-0' },
    { direction: 'prev', navSize: 'overlay', className: '-left-8' },
    { direction: 'next', navSize: 'overlay', className: '-right-8' },
  ],
  defaultVariants: { position: 'mid' },
});

/** The chevron, on the same axis as the control it sits in. */
const carouselNavIconVariants = cva('', {
  variants: {
    direction: { prev: '', next: '' },
    navSize: {
      base: 'h-4 w-4',
      md: 'size-5',
      lg: 'size-5',
      responsive: 'size-5',
      overlay: 'text-foreground-inverted size-6',
    },
  },
  compoundVariants: [
    { direction: 'prev', navSize: 'overlay', className: 'relative left-2' },
    { direction: 'next', navSize: 'overlay', className: 'relative right-2' },
  ],
});

/**
 * The private spelling of the axis below, and the one the `satisfies` clause
 * names.
 *
 * Naming the EXPORTED alias there instead would annotate a const with a type
 * that shares its identifier, and Storybook's react-docgen resolves such a type
 * reference back to the const it annotates — a cycle it follows until the stack
 * runs out, taking the dev server's whole module graph with it.
 */
type CarouselNavSizeName = NonNullable<VariantProps<typeof carouselNavVariants>['navSize']>;

export type CarouselNavSize = CarouselNavSizeName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const CarouselNavSize = {
  /** The smallest target. */
  BASE: 'base',
  /** A phone-sized target. */
  MD: 'md',
  /** The desktop target. */
  LG: 'lg',
  /** Grows with the viewport. */
  RESPONSIVE: 'responsive',
  /** Hung over the artwork. */
  OVERLAY: 'overlay',
} as const satisfies Record<string, CarouselNavSizeName>;

export type CarouselNavProps = ComponentProps<typeof Button> & {
  isInfinite?: boolean;
  position?: 'free' | 'mid';
  /** Required and closed: how big the control is, and how it is treated. */
  navSize: CarouselNavSize;
  iconClassName?: ClassValue;
};

function CarouselPrevious({
  className,
  variant = 'secondary',
  size = 'icon',
  position = 'mid',
  navSize,
  iconClassName,
  isInfinite = false,
  ...props
}: CarouselNavProps) {
  const { scrollPrev, canScrollPrev } = useCarousel();

  return (
    <Button
      {...props}
      data-slot="carousel-previous"
      variant={variant}
      size={size}
      disabled={canScrollPrev === undefined ? true : !isInfinite && !canScrollPrev}
      className={clsxm(carouselNavVariants({ direction: 'prev', position, navSize }), className)}
      onClick={scrollPrev}
    >
      <ChevronLeft
        className={clsxm(carouselNavIconVariants({ direction: 'prev', navSize }), iconClassName)}
      />
      <span className="sr-only">Previous slide</span>
    </Button>
  );
}

function CarouselNext({
  className,
  variant = 'secondary',
  size = 'icon',
  position = 'mid',
  navSize,
  isInfinite = false,
  iconClassName,
  ...props
}: CarouselNavProps) {
  const { scrollNext, canScrollNext } = useCarousel();

  return (
    <Button
      {...props}
      data-slot="carousel-next"
      variant={variant}
      size={size}
      disabled={canScrollNext === undefined ? true : !isInfinite && !canScrollNext}
      className={clsxm(carouselNavVariants({ direction: 'next', position, navSize }), className)}
      onClick={scrollNext}
    >
      <ChevronRight
        className={clsxm(carouselNavIconVariants({ direction: 'next', navSize }), iconClassName)}
      />
      <span className="sr-only">Next slide</span>
    </Button>
  );
}

// --- Dots -----------------------------------------------------------------
// `CarouselDot` is one presentational dot button; `CarouselDots` (below) is the
// stateful strip that maps embla's scroll snaps to a row of them.

// One styled dot indicator, two looks. `bar` is the thin progress bar (homepage
// banner / review strips, meant to sit on imagery — hence white/translucent);
// `dot` is the round pip. The active look is a compound of `variant` × `active`
// so each variant keeps its own highlight.
const carouselDotVariants = cva('', {
  variants: {
    variant: {
      bar: 'bg-background text-foreground-inverted flex h-0.5 w-12 cursor-pointer items-center justify-center opacity-20 before:absolute before:inset-x-0 before:inset-y-[-1rem] before:content-[""]',
      dot: 'bg-foreground/20 h-3 w-3 rounded-full transition-all',
    },
    active: { true: '', false: '' },
  },
  compoundVariants: [
    { variant: 'bar', active: true, className: 'opacity-100' },
    {
      variant: 'dot',
      active: true,
      className:
        'bg-primary ring-primary ring-offset-background cursor-pointer ring-2 ring-offset-2',
    },
  ],
  defaultVariants: { variant: 'bar', active: false },
});

// Responsive visibility is its own dimension, independent of the dot look.
// `hideFrom` sets the breakpoint at (and above) which the dot is hidden; default
// `none` keeps it visible at every size. Works for any variant — e.g. a bar can
// be hidden from `tab` up, a pip kept visible everywhere.
const carouselDotWrapperVariants = cva('relative', {
  variants: {
    hideFrom: {
      none: '',
      tab: 'tab:hidden',
      lap: 'lap:hidden',
      des: 'des:hidden',
    },
  },
  defaultVariants: { hideFrom: 'none' },
});

export type CarouselDotVariant = NonNullable<VariantProps<typeof carouselDotVariants>['variant']>;
export type CarouselDotHideFrom = NonNullable<
  VariantProps<typeof carouselDotWrapperVariants>['hideFrom']
>;

export type CarouselDotProps = Omit<ComponentProps<'button'>, 'className'> &
  Pick<VariantProps<typeof carouselDotVariants>, 'variant'> & {
    current?: boolean;
    // Breakpoint at/above which the dot hides; `none` (default) = always visible.
    // Orthogonal to `variant`.
    hideFrom?: CarouselDotHideFrom | undefined;
    className?: ClassValue;
    classNameWrapper?: ClassValue;
  };

function CarouselDot({
  className,
  classNameWrapper,
  variant = 'bar',
  hideFrom,
  current,
  children,
  ...props
}: CarouselDotProps) {
  const active = current === true;

  return (
    <div
      data-slot="carousel-dot"
      className={clsxm(carouselDotWrapperVariants({ hideFrom }), classNameWrapper)}
    >
      <button
        data-slot="carousel-dot-button"
        data-current={active ? '' : undefined}
        className={clsxm(carouselDotVariants({ variant, active }), className)}
        {...props}
      >
        {children}
      </button>
    </div>
  );
}

const carouselDotCardVariants = cva('group des:px-6 des:py-6 cursor-pointer px-3 py-4 text-left', {
  variants: {
    active: { true: 'bg-primary', false: 'hover:bg-primary' },
  },
  defaultVariants: { active: false },
});

const carouselDotCardCategoryVariants = cva('des:text-xs font-semibold tracking-widest uppercase', {
  variants: {
    active: {
      true: 'text-foreground-muted',
      false: 'text-primary group-hover:text-foreground-muted',
    },
  },
  defaultVariants: { active: false },
});

const carouselDotCardHeadingVariants = cva('font-heading des:text-lg line-clamp-2 text-base', {
  variants: {
    active: { true: 'text-foreground', false: 'group-hover:text-foreground' },
  },
  defaultVariants: { active: false },
});

export type CarouselDotCardProps = ComponentProps<'button'> & {
  current?: boolean;
  category: string;
  heading: string;
};

// App-specific content dot for the homepage articles carousel: a category +
// heading tab rather than a plain pip.
function CarouselDotCard({ current, category, heading, ...props }: CarouselDotCardProps) {
  const active = current === true;

  return (
    <button
      data-slot="carousel-dot-card"
      className={carouselDotCardVariants({ active })}
      {...props}
    >
      <div className="des:gap-y-3 flex flex-col gap-y-1">
        <div className={carouselDotCardCategoryVariants({ active })}>{category}</div>
        <div className={carouselDotCardHeadingVariants({ active })}>{heading}</div>
      </div>
    </button>
  );
}

// Embla-native dot state, the official `useDotButton` pattern: dots are driven
// by `scrollSnapList()` (one entry per scroll position, NOT per slide — correct
// for grouped / `slidesToScroll: 'auto'` carousels), the active index by
// `selectedScrollSnap()`, and clicks by `scrollTo(index)`. Replaces the old
// `cloneElement`-based prop injection.
export function useCarouselDots(api: CarouselApi) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api]
  );

  useEffect(() => {
    if (!api) return;

    const onInit = (emblaApi: CarouselApi) => {
      if (emblaApi) setScrollSnaps(emblaApi.scrollSnapList());
    };
    const onSelect = (emblaApi: CarouselApi) => {
      if (emblaApi) setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    onInit(api);
    onSelect(api);
    api.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);

    return () => {
      api.off('reInit', onInit).off('reInit', onSelect).off('select', onSelect);
    };
  }, [api]);

  return { selectedIndex, scrollSnaps, onDotButtonClick };
}

type CarouselDotsProps = Omit<ComponentProps<'div'>, 'children'> & {
  variant?: CarouselDotVariant;
  // Breakpoint at/above which each dot hides (forwarded to every CarouselDot).
  // Default `none` = always visible; orthogonal to `variant`.
  hideFrom?: CarouselDotHideFrom | undefined;
  interactionMode?: 'click' | 'hover';
  classNameDot?: ClassValue;
  classNameDotWrapper?: ClassValue;
  // Per-dot content (e.g. an sr-only slide label). Receives the snap index and
  // whether it is the active snap.
  children?: (arg: { index: number; selected: boolean }) => ReactNode;
};

// Batteries-included dot strip: reads the carousel `api` from context and maps
// the embla scroll-snap list to styled `CarouselDot`s. Because it consumes
// context internally, server-component callers can render it (and pass only
// serializable props) without becoming client components themselves. For bespoke
// layouts (e.g. text-card navigation) call `useCarouselDots(api)` directly.
function CarouselDots({
  className,
  classNameDot,
  classNameDotWrapper,
  variant = 'bar',
  hideFrom,
  interactionMode = 'click',
  children,
  ...props
}: CarouselDotsProps) {
  const { api } = useCarousel();
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useCarouselDots(api);

  return (
    <div className={clsxm(className)} data-slot="carousel-dots" {...props}>
      {scrollSnaps.map((_, index) => {
        const selected = index === selectedIndex;
        const interaction =
          interactionMode === 'hover'
            ? { onMouseEnter: () => onDotButtonClick(index) }
            : { onClick: () => onDotButtonClick(index) };

        return (
          <CarouselDot
            key={index}
            variant={variant}
            hideFrom={hideFrom}
            current={selected}
            className={classNameDot}
            classNameWrapper={classNameDotWrapper}
            {...interaction}
          >
            {children?.({ index, selected })}
          </CarouselDot>
        );
      })}
    </div>
  );
}

export {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselDot,
  CarouselDotCard,
  CarouselDots,
  carouselDotVariants,
  CarouselItem,
  CarouselNext,
  carouselNavVariants,
  CarouselPrevious,
};
